import type { AxiosInstance } from 'axios';
import type { ApiListResponse, ApiResponse, ApiPaginationResponse } from '@/types';
import type {
  CreateMemberPayload,
  EditMember,
  MatchMemberItem,
  Member,
  MemberDetail,
  MemberListQuery,
  MemberQuery,
  MemberQueryV2,
  MemberV2,
  MtrItem,
  MtrListQuery,
  UpdateMemberDetailPayload,
  CasinoGamesData,
  CasinoListRespose,
  CasinoListQuery,
  MemberBlacklistQuery,
  AddBlackMemberQuery,
  MemberBlackItem,
  MemberSelfExclusionQuery,
  MemberSelfExclusionResponse,
  SelfExclusionByMemberQuery,
  UpdateSelfExclusionPayload,
  MemberBetCountResponse,
  MemberBlacklistReportQuery,
  MemberBlacklistReportItem,
  MemberTagListResponse,
  GetMemberBlackListRelatedUsersResponse,
  GetMemberBlackRulesResponse,
  MemberBlackRulesItem,
  MemberWhitelistQuery,
  MemberWhiteItem,
  AddWhiteMemberQuery
} from '@/types/api/member.type';

export default function memberListApi(ai: AxiosInstance) {
  function getMatchMember<D extends MatchMemberItem[], R extends ApiListResponse<D>>(name: string) {
    return ai.get<R>('/memberList/getMatchMember', { params: { matchName: name } });
  }

  // 创建MemberList
  function createMemberList<D, R extends ApiListResponse<D>>() {
    return ai.post<R>('/memberList/createMemberList');
  }

  // 用uuid查询MemberList
  function findMemberList<D extends { member: Member, }, R extends ApiListResponse<D>>(uuid: Member['uuid']) {
    return ai.get<R>('/memberList/findMemberList', { params: { uuid } });
  }

  // 分页获取MemberList列表
  function getMemberListList<D extends Member[], R extends ApiListResponse<D>>(query: MemberListQuery) {
    return ai.get<R>('/memberList/getMemberListList', { params: query });
  }

  // 用uuid強制登出用戶
  function logOutMemberList<D extends Record<string, never>, R extends ApiListResponse<D>>(uuid: string) {
    return ai.post<R>('/memberList/logOutMemberList', { uuid });
  }

  // 更新MemberList
  function updateMemberList<D extends Record<string, never>, R extends ApiResponse<D>>(payload: EditMember) {
    return ai.put<R>('/memberList/updateMemberList', payload);
  }

  // 获取用户列表信息
  function getMemberList<D extends Member[], R extends ApiListResponse<D>>(payload: MemberQuery) {
    return ai.get<R>('/memberList/getMemberList', { params: payload });
  }

  // 获取用户列表信息
  function getMemberListV2<D extends MemberV2[], R extends ApiListResponse<D>>(payload: MemberQueryV2) {
    return ai.get<R>('/api/v2/member/listV2', { params: payload });
  }

  // 获取用户统计信息
  function getMemberBetCount<D extends MemberBetCountResponse, R extends ApiResponse<D>>(payload: MemberQueryV2) {
    return ai.get<R>('/api/v2/member/betStats', { params: payload });
  }

  // 获取用户列表信息-full
  function getMemberFullListV2<D extends MemberV2[], R extends ApiListResponse<D>>(payload: MemberQueryV2) {
    return ai.get<R>('/api/v2/s/member/listV2', { params: payload });
  }

  // 踢出用户
  function logOutMember<D extends Record<string, never>, R extends ApiResponse<D>>(memberId: string, uuid: string) {
    return ai.post<R>('/memberList/logOutMember', { memberId, uuid });
  }

  // 创建用户
  function createMember<D, R extends ApiListResponse<D>>(payload: CreateMemberPayload) {
    return ai.post<R>('/memberList/createMember', payload);
  }

  // 获取用户详情
  function findMemberDetail<D extends MemberDetail, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/memberList/findMemberDetail', { params: { id } });
  }

  // 修改用户信息
  function updateMemberDetail<D extends Record<string, never>, R extends ApiResponse<D>>(payload: UpdateMemberDetailPayload) {
    return ai.put<R>('memberList/updateMemberDetail', payload);
  }

  // 获取用户操作信息
  function getMtrList<D extends MtrItem[], R extends ApiListResponse<D>>(params: MtrListQuery) {
    return ai.get<R>('memberList/getMtrList', { params });
  }

  //下载用户列表
  function downloadMembers<R extends BlobPart>(params: MemberQueryV2) {
    return ai.get<R>('/api/v2/member/downloadMembersV2', { params });
  }

  //下载用户列表-full
  function downloadMembersFull<R extends BlobPart>(params: MemberQueryV2) {
    return ai.get<R>('/api/v2/s/member/downloadMembersV2', { params });
  }

  // 获得casino games列表
  function getCasinoGameList<D extends CasinoGamesData, R extends ApiResponse<D>>() {
    return ai.get<R>('/api/v2/casino/gameList', { params: { lang: 'en' } });
  }

  // 获得casino 投注记录列表
  function getCasinoHistoryList<D extends CasinoListRespose, R extends ApiResponse<D>>(params: CasinoListQuery) {
    return ai.get<R>('/api/v2/casino/history', { params });
  }

  // 下载投注记录列表
  function DownloadCasinoHistoryList<R extends BlobPart>(params: CasinoListQuery) {
    return ai.get<R>('/api/v2/casino/downloadTicketsHistory', { params });
  }

  function getMemberBlacklist<D extends MemberBlackItem[], R extends ApiListResponse<D>>(payload: MemberBlacklistQuery) {
    return ai.get<R>('/api/v2/memberBlacklist/getList', { params: payload });
  }

  function addMemberBlackList<D, R extends ApiListResponse<D>>(payload: AddBlackMemberQuery) {
    return ai.post<R>('/api/v2/memberBlacklist/add', payload);
  }

  function deleteBlackList<D extends Record<string, never>, R extends ApiResponse<D>>(id: number) {
    return ai.post<R>('/api/v2/memberBlacklist/delete', { id });
  }

  function downloadMemberBlacklist<R extends BlobPart>(params: MemberBlacklistQuery) {
    return ai.get<R>('/api/v2/memberBlacklist/download', { params });
  }

  function getMemberBlacklistReport<D extends MemberBlacklistReportItem[], R extends ApiListResponse<D>>(payload: MemberBlacklistReportQuery) {
    return ai.get<R>('/api/v2/memberBlacklist/total', { params: payload });
  }

  function getMemberBlackListRelatedUsers<D extends GetMemberBlackListRelatedUsersResponse, R extends ApiResponse<D>>(payload: MemberBlacklistReportQuery) {
    return ai.get<R>('/api/v2/memberBlacklist/relatedUsers', { params: payload });
  }

  function getMemberBlackRules<D extends GetMemberBlackRulesResponse, R extends ApiResponse<D>>() {
    return ai.get<R>('/api/v2/memberBlacklist/rules');
  }

  function setMemberBlackRules<D extends Record<string, never>, R extends ApiResponse<D>>(items: MemberBlackRulesItem[]) {
    return ai.post<R>('/api/v2/memberBlacklist/rules', { rules: items });
  }

  function downloadMemberBlacklistReport<R extends BlobPart>(params: MemberBlacklistReportQuery) {
    return ai.get<R>('/api/v2/memberBlacklist/downloadTotal', { params });
  }

  function getBetsLip<D extends Record<string, never>, R extends ApiResponse<D>>(ticketId: string) {
    return ai.get<R>('/api/v2/casino/betslip', { params: { ticketId } });
  }

  function getSelfExclusionMemberList<D extends MemberSelfExclusionResponse, R extends ApiPaginationResponse<D>>(payload: MemberSelfExclusionQuery) {
    return ai.get<R>('/api/v2/selfExclusion/getList', { params: payload });
  }

  function getSelfExclusionByMember<D extends MemberSelfExclusionResponse, R extends ApiPaginationResponse<D>>(payload: SelfExclusionByMemberQuery) {
    return ai.get<R>('/api/v2/selfExclusion/getListByMember', { params: payload });
  }

  function updateSelfExclusionStatus<D extends Record<string, never>, R extends ApiResponse<D>>(payload: UpdateSelfExclusionPayload) {
    return ai.post<R>('/api/v2/selfExclusion/updateStatus', payload);
  }

  function downloadSelfExclusionList<R extends BlobPart>(params: MemberSelfExclusionQuery) {
    return ai.get<R>('/api/v2/selfExclusion/downloadList', { params });
  }

  // 用id获取用户标签列表
  function getMemberTagList<D extends MemberTagListResponse, R extends ApiResponse<D>>(id: string) {
    return ai.get<R>('/api/v2/member/tagList', { params: { id } });
  }

  //白名单列表
  function getMemberWhitelist<D extends MemberWhiteItem[], R extends ApiListResponse<D>>(params: MemberWhitelistQuery) {
    return ai.get<R>('/api/v2/memberWhitelist/getList', { params });
  }

  //删除白名单
  function deleteWhiteList<D extends Record<string, never>, R extends ApiResponse<D>>(id: number) {
    return ai.post<R>('/api/v2/memberWhitelist/delete', { id });
  }

  //下载白名单
  function downloadMemberWhitelist<R extends BlobPart>(params: MemberWhitelistQuery) {
    return ai.get<R>('/api/v2/memberWhitelist/download', { params });
  }

  //添加白名单
  function addMemberWhiteList<D, R extends ApiListResponse<D>>(payload: AddWhiteMemberQuery) {
    return ai.post<R>('/api/v2/memberWhitelist/add', payload);
  }

  return {
    getMemberList,
    getMemberListV2,
    getMemberBetCount,
    getMemberFullListV2,
    getMatchMember,
    createMemberList,
    findMemberList,
    getMemberListList,
    logOutMemberList,
    updateMemberList,
    logOutMember,
    createMember,
    findMemberDetail,
    updateMemberDetail,
    getMtrList,
    downloadMembers,
    downloadMembersFull,
    getCasinoGameList,
    getCasinoHistoryList,
    DownloadCasinoHistoryList,
    getMemberBlacklist,
    addMemberBlackList,
    deleteBlackList,
    downloadMemberBlacklist,
    getBetsLip,
    getSelfExclusionMemberList,
    getSelfExclusionByMember,
    updateSelfExclusionStatus,
    downloadSelfExclusionList,
    getMemberBlacklistReport,
    getMemberBlackListRelatedUsers,
    downloadMemberBlacklistReport,
    getMemberTagList,
    getMemberBlackRules,
    setMemberBlackRules,
    getMemberWhitelist,
    deleteWhiteList,
    downloadMemberWhitelist,
    addMemberWhiteList
  };
}
